import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, type SelectItem, TextInput } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import RevAudDetailsForm from "src/components/pages/Summary/ReviewsAndAudits/RevAudDetailsForm";
import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { Button } from "src/components/shared/Button/Button";
import { useGetApi, usePostApi } from "src/hooks";
import { useSelector } from "src/redux/hooks";
import { ButtonsWrapper, LayoutContainer, PageContainer } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { type IRevAudForm } from "./RevAudCreate.types";
import {
  mapSelectsData,
  revAudInitialValues,
  revAudValidationSchema,
} from "./RevAudCreate.utils";

const RevAudCreate = () => {
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const clientid = useSelector(state => state.permsSlice.clientid);
  const isAuditsPage = useMemo(() => pathname.includes("audits"), [pathname]);

  const [emails, setEmails] = useState<SelectItem[]>([]);
  const [newEmail, setNewEmail] = useState("");

  const mapper = useCallback((data: any) => {
    const result = mapSelectsData(data);
    return result;
  }, []);

  const { data } = useGetApi({
    request: "get-reviews",
    mapper,
  });

  const { post, isLoading } = usePostApi();

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<IRevAudForm>({
    values: revAudInitialValues,
    resolver: yupResolver(revAudValidationSchema),
  });

  const handleAddEmail = () => {
    setEmails(prev => [{ label: newEmail, value: newEmail }, ...prev]);
    setValue("emails", [newEmail, ...getValues("emails")]);
    setNewEmail("");
  };

  const onSubmit: SubmitHandler<IRevAudForm> = async data => {
    await post({
      request: "save-audit",
      body: {
        params: {
          ...data,
          type: Number(data.type),
          id: "new",
          yachtid: getYachtId(),
          clientid,
        },
      },
      onSuccess: () => {
        Notification(
          "Success",
          `The ${
            isAuditsPage ? "audit" : "review"
          } has been created successfully`,
          "green",
        );
        navigate(`/summary/${isAuditsPage ? "audits" : "reviews"}`);
      },
    });
  };

  return (
    <PageContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Breadcrumbs>
          <ButtonsWrapper>
            <Button
              type="submit"
              leftIcon={<IconDeviceFloppy size={"1.4rem"} />}
              variant="outline"
              loading={isLoading}>
              Save
            </Button>
          </ButtonsWrapper>
        </Breadcrumbs>
        <LayoutContainer>
          <Flex style={{ flex: 1 }} direction="column" align="center">
            <CheckboxGroup
              title="E-mails"
              data={emails}
              control={control}
              name="emails"
              scrollHeight={500}
              error={!!errors.emails}>
              <Flex w="60%" align="center" justify="space-between">
                <TextInput
                  placeholder="Add different e-mail"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                />
                <Button onClick={handleAddEmail}>Add</Button>
              </Flex>
            </CheckboxGroup>
          </Flex>
          <Flex style={{ flex: 1 }} direction="column" align="center">
            <CheckboxGroup
              title="Logbooks"
              data={data?.logbooks || []}
              control={control}
              name="logbooks"
              scrollHeight={500}
              error={!!errors.logbooks}
            />
          </Flex>
          <Flex style={{ flex: 1 }} direction="column" align="center" gap="lg">
            <RevAudDetailsForm
              setValue={setValue}
              errors={errors}
              control={control}
              register={register}
            />
          </Flex>
        </LayoutContainer>
      </form>
    </PageContainer>
  );
};

export default RevAudCreate;
